import type { SearchParams } from '@/server/searchParams.ts';
import type { Filter, FilterDataMultiSelect, FilterDataOption } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { Facilities, FacilityEntry } from '@/app/components/search/utils/api/filtersAPIResponseTypes.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FACILITY_GROUPS, type FacilityGroup } from '@/app/common/filters/groups/facilityGroups.ts';
import { getSelectedValues } from '@/app/common/filters/groups/filterGroupsUtils.ts';
import { disabledAtZero } from '@/app/common/filters/groups/optionCounts.ts';
import { OPTIONS_SORT, sortOptions } from '@/app/common/filters/types/shared/utils/sort.ts';

type CreateFacilityGroupFilters = {
  searchParams: SearchParams;
  facilities: Facilities | undefined;
  translationDictionary: TranslationDictionary;
};

type FacilityEntries = [string, FacilityEntry][];

const entriesByGroup = (facilities: Facilities): Map<string, FacilityEntries> =>
  Object.entries(facilities).reduce((groups, entry) => {
    const [, { group }] = entry;
    return groups.set(group, [...(groups.get(group) ?? []), entry]);
  }, new Map<string, FacilityEntries>());

const groupOptions = (entries: FacilityEntries, selectedValues: string[]): FilterDataOption[] =>
  sortOptions(
    OPTIONS_SORT.AUTO,
    entries.map(([value, entry]) => {
      const selected = selectedValues.includes(value);
      return {
        value,
        label: entry.label,
        counter: entry.count,
        selected,
        disabled: disabledAtZero(selected, entry.count),
      };
    })
  );

const groupFilter = (
  group: FacilityGroup,
  entries: FacilityEntries,
  searchParams: SearchParams,
  translationDictionary: TranslationDictionary
): Filter | undefined => {
  const options = groupOptions(entries, getSelectedValues({ name: group.name, searchParams }));
  if (!options.length) return undefined;

  return {
    name: group.name,
    type: FILTER_TYPE.MULTI_SELECT,
    data: {
      label: getTranslationServer(group.titleKey, translationDictionary),
      options,
    } satisfies FilterDataMultiSelect,
  };
};

export const createFacilityGroupFilters = ({
  searchParams,
  facilities,
  translationDictionary,
}: CreateFacilityGroupFilters): Filter[] => {
  if (!facilities) return [];

  const grouped = entriesByGroup(facilities);

  return FACILITY_GROUPS.map((group) =>
    groupFilter(group, grouped.get(group.id) ?? [], searchParams, translationDictionary)
  ).filter((filter): filter is Filter => !!filter);
};
